import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { CardModule } from 'primeng/card';
import { PasswordModule } from 'primeng/password';
import { MessageModule } from 'primeng/message';
import { DividerModule } from 'primeng/divider';
import { ToastModule } from 'primeng/toast';
import { TagModule } from 'primeng/tag';
import { FileUploadModule } from 'primeng/fileupload';
import { AvatarModule } from 'primeng/avatar';
import { MessageService } from 'primeng/api';
import { AuthService } from '../../service/auth.service';
import { UserService } from '../../service/user.service';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-profile',
  standalone: true,
  imports: [
    CommonModule, 
    FormsModule, 
    ButtonModule, 
    InputTextModule, 
    CardModule,
    PasswordModule,
    MessageModule,
    DividerModule,
    ToastModule,
    TagModule,
    FileUploadModule,
    AvatarModule
  ],
  providers: [],
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.css']
})
export class ProfileComponent implements OnInit {
  // Informations personnelles
  username: string = '';
  email: string = '';
  phone: string = '';
  phoneFixe: string = '';
  photoUrl: string = '';
  role: string = '';
  userId: number = 0;
  originalUsername: string = '';
  originalEmail: string = '';
  originalPhone: string = '';
  originalPhoneFixe: string = '';
  originalPhotoUrl: string = '';
  selectedFile: File | null = null;
  photoPreview: string | null = null;

  // Changement de mot de passe
  currentPassword: string = '';
  newPassword: string = '';
  confirmPassword: string = '';
  passwordMessage: { severity: string; text: string } | null = null;
  
  // États de chargement
  isSavingProfile: boolean = false;
  isChangingPassword: boolean = false;
  isUploadingPhoto: boolean = false;
  isDeletingPhoto: boolean = false;

  constructor(
    private authService: AuthService,
    private userService: UserService,
    private router: Router,
    private messageService: MessageService
  ) {}

  ngOnInit() {
    this.loadUserInfo();
  }

  loadUserInfo() {
    // Charger d'abord depuis localStorage
    this.username = localStorage.getItem('username') || '';
    this.email = localStorage.getItem('email') || '';
    this.phone = localStorage.getItem('phone') || '';
    this.phoneFixe = localStorage.getItem('phone_fixe') || '';
    this.photoUrl = localStorage.getItem('photo_url') || '';
    this.role = localStorage.getItem('role') || '';
    const userIdStr = localStorage.getItem('user_id');
    this.userId = userIdStr ? parseInt(userIdStr, 10) : 0;
    
    // Conserver les valeurs originales
    this.originalUsername = this.username;
    this.originalEmail = this.email;
    this.originalPhone = this.phone;
    this.originalPhoneFixe = this.phoneFixe;
    this.originalPhotoUrl = this.photoUrl;

    // Charger les données depuis l'API pour avoir les infos à jour
    if (this.userId) {
      this.userService.getMyProfile().subscribe({
        next: (user) => {
          this.username = user.username;
          this.email = user.email;
          this.phone = user.telephone || '';
          this.phoneFixe = user.telephone_fixe || '';
          this.photoUrl = user.photo_url || '';
          this.role = user.role;
          
          // Mettre à jour localStorage
          localStorage.setItem('username', this.username);
          localStorage.setItem('email', this.email);
          localStorage.setItem('phone', this.phone);
          localStorage.setItem('phone_fixe', this.phoneFixe);
          localStorage.setItem('photo_url', this.photoUrl);
          localStorage.setItem('role', this.role);
          
          // Mettre à jour les valeurs originales
          this.originalUsername = this.username;
          this.originalEmail = this.email;
          this.originalPhone = this.phone;
          this.originalPhoneFixe = this.phoneFixe;
          this.originalPhotoUrl = this.photoUrl;
        },
        error: (error) => {
          console.error('Erreur lors du chargement du profil:', error);
        }
      });
    }
  }

  getRoleBadge(): string {
    switch (this.role) {
      case 'ADMIN':
      case 'admin':
        return 'Admin';
      case 'COMMERCIAL':
        return 'Commercial';
      case 'VIEWER':
      case 'USER':
        return 'Utilisateur';
      default:
        return '';
    }
  }

  getRoleSeverity(): 'success' | 'info' | 'warning' | 'danger' {
    switch (this.role) {
      case 'ADMIN':
      case 'admin':
        return 'danger';
      case 'COMMERCIAL':
        return 'info';
      default:
        return 'success';
    }
  }

  hasProfileChanges(): boolean {
    return this.username !== this.originalUsername ||
           this.email !== this.originalEmail ||
           this.phone !== this.originalPhone ||
           this.phoneFixe !== this.originalPhoneFixe;
  }

  resetProfile(): void {
    this.username = this.originalUsername;
    this.email = this.originalEmail;
    this.phone = this.originalPhone;
    this.phoneFixe = this.originalPhoneFixe;
  }

  saveProfile() {
    // Validation
    if (!this.username || !this.email) {
      this.messageService.add({
        severity: 'error',
        summary: 'Erreur',
        detail: 'Le nom d\'utilisateur et l\'email sont obligatoires',
        life: 3000
      });
      return;
    }

    // Validation email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(this.email)) {
      this.messageService.add({
        severity: 'error',
        summary: 'Erreur',
        detail: 'Veuillez entrer une adresse email valide',
        life: 3000
      });
      return;
    }

    this.isSavingProfile = true;
    
    // Appel réel à l'API
    const userData = {
      username: this.username,
      email: this.email,
      telephone: this.phone,
      telephone_fixe: this.phoneFixe
    };

    this.userService.updateMyProfile(userData).subscribe({
      next: (user) => {
        // Mettre à jour localStorage
        localStorage.setItem('username', user.username);
        localStorage.setItem('email', user.email);
        localStorage.setItem('phone', user.telephone || '');
        localStorage.setItem('phone_fixe', user.telephone_fixe || '');
        
        // Mettre à jour les valeurs originales
        this.originalUsername = user.username;
        this.originalEmail = user.email;
        this.originalPhone = user.telephone || '';
        this.originalPhoneFixe = user.telephone_fixe || '';
        
        // Mettre à jour les valeurs actuelles
        this.username = user.username;
        this.email = user.email;
        this.phone = user.telephone || '';
        this.phoneFixe = user.telephone_fixe || '';
        
        this.isSavingProfile = false;
        
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: 'Vos informations ont été enregistrées avec succès',
          life: 3000
        });
      },
      error: (error) => {
        this.isSavingProfile = false;
        
        let errorMessage = 'Une erreur est survenue lors de la sauvegarde';
        if (error.error?.detail) {
          errorMessage = error.error.detail;
        }
        
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: errorMessage,
          life: 4000
        });
      }
    });
  }

  changePassword() {
    // Réinitialiser le message
    this.passwordMessage = null;

    // Validation
    if (!this.currentPassword || !this.newPassword || !this.confirmPassword) {
      this.passwordMessage = {
        severity: 'error',
        text: 'Tous les champs sont obligatoires'
      };
      return;
    }

    if (this.newPassword !== this.confirmPassword) {
      this.passwordMessage = {
        severity: 'error',
        text: 'Les mots de passe ne correspondent pas'
      };
      return;
    }

    if (this.newPassword.length < 8) {
      this.passwordMessage = {
        severity: 'error',
        text: 'Le mot de passe doit contenir au moins 8 caractères'
      };
      return;
    }

    // Validation de la complexité
    const hasUpperCase = /[A-Z]/.test(this.newPassword);
    const hasLowerCase = /[a-z]/.test(this.newPassword);
    const hasNumbers = /\d/.test(this.newPassword);

    if (!hasUpperCase || !hasLowerCase || !hasNumbers) {
      this.passwordMessage = {
        severity: 'warn',
        text: 'Le mot de passe devrait contenir des majuscules, minuscules et chiffres'
      };
      return;
    }

    this.isChangingPassword = true;

    // Appel réel à l'API
    const passwordData = {
      current_password: this.currentPassword,
      new_password: this.newPassword
    };

    this.userService.changePassword(passwordData).subscribe({
      next: () => {
        this.isChangingPassword = false;
        this.passwordMessage = null;
        
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: 'Votre mot de passe a été changé avec succès',
          life: 3000
        });
        
        // Réinitialiser les champs
        this.currentPassword = '';
        this.newPassword = '';
        this.confirmPassword = '';
      },
      error: (error) => {
        this.isChangingPassword = false;
        
        let errorMessage = 'Une erreur est survenue lors du changement de mot de passe';
        if (error.error?.detail) {
          errorMessage = error.error.detail;
        } else if (error.status === 401) {
          errorMessage = 'Le mot de passe actuel est incorrect';
        }
        
        this.passwordMessage = {
          severity: 'error',
          text: errorMessage
        };
        
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: errorMessage,
          life: 4000
        });
      }
    });
  }

  isPasswordFormValid(): boolean {
    return !!(this.currentPassword && this.newPassword && this.confirmPassword);
  }

  onFileSelect(event: any): void {
    const file = event.files[0];
    if (file) {
      this.selectedFile = file;
      
      // Créer une prévisualisation
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.photoPreview = e.target.result;
      };
      reader.readAsDataURL(file);
      
      // Upload automatique
      this.uploadPhoto();
    }
  }

  uploadPhoto(): void {
    if (!this.selectedFile) {
      return;
    }

    this.isUploadingPhoto = true;
    const formData = new FormData();
    formData.append('file', this.selectedFile);

    this.userService.uploadProfilePhoto(formData).subscribe({
      next: (response) => {
        this.photoUrl = response.photo_url;
        this.originalPhotoUrl = response.photo_url;
        this.photoPreview = null;
        this.selectedFile = null;
        
        // Mettre à jour localStorage
        localStorage.setItem('photo_url', this.photoUrl);
        
        this.isUploadingPhoto = false;
        
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: 'Photo de profil mise à jour avec succès',
          life: 3000
        });
      },
      error: (error) => {
        this.isUploadingPhoto = false;
        this.photoPreview = null;
        this.selectedFile = null;
        
        let errorMessage = 'Erreur lors de l\'upload de la photo';
        if (error.error?.detail) {
          errorMessage = error.error.detail;
        }
        
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: errorMessage,
          life: 4000
        });
      }
    });
  }

  deletePhoto(): void {
    if (!this.photoUrl) {
      return;
    }

    this.isDeletingPhoto = true;

    this.userService.deleteProfilePhoto().subscribe({
      next: () => {
        this.photoUrl = '';
        this.originalPhotoUrl = '';
        this.photoPreview = null;
        
        // Mettre à jour localStorage
        localStorage.removeItem('photo_url');
        
        this.isDeletingPhoto = false;
        
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: 'Photo de profil supprimée avec succès',
          life: 3000
        });
      },
      error: (error) => {
        this.isDeletingPhoto = false;
        
        let errorMessage = 'Erreur lors de la suppression de la photo';
        if (error.error?.detail) {
          errorMessage = error.error.detail;
        }
        
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: errorMessage,
          life: 4000
        });
      }
    });
  }

  getPhotoUrl(): string {
    if (this.photoPreview) {
      return this.photoPreview;
    }
    if (this.photoUrl) {
      // Si c'est un chemin relatif, ajouter l'URL de base de l'API
      if (this.photoUrl.startsWith('/api/')) {
        return `${environment.apiUrl}${this.photoUrl}`;
      }
      return this.photoUrl;
    }
    return '';
  }

  getUserInitials(): string {
    if (this.username) {
      const names = this.username.split(' ');
      if (names.length >= 2) {
        return names[0].charAt(0).toUpperCase() + names[1].charAt(0).toUpperCase();
      }
      return this.username.substring(0, 2).toUpperCase();
    }
    return 'U';
  }
}
